/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-disable react-refresh/only-export-components --
 * This Page Designer component file intentionally co-exports the `Hero` component with the
 * shared `widenVideoFileUrl` helper (consumed by content-container / simple-video) and the
 * `HeroMetadata` PD class (consumed by its metadata test). Fast Refresh does not apply to PD
 * components, and the config already turns this rule off for other required-co-export files. */
import { type ReactElement } from 'react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { cn } from '@/lib/utils';
import { defaultSettingsProps, type DefaultSettingsValue } from '@/lib/page-designer/default-settings';
import { type RipperPaperTone } from '@/components/ripper-paper';
import { type WidenImageValue } from '@/components/widen-image';
// Raw `/images/...` paths work in dev but 404 on Managed Runtime — import as Vite assets.
// The design only ships the light hero paper (solid sheet, torn bottom edge only). The dark tone
// reuses the same sheet with a CSS `invert`, which flips the paper light→dark AND the torn-bottom
// reveal dark→light — keeping the tone logic consistent without a separate dark asset.
import paperTexture from '/images/ripper-paper/paper-light.png';

/**
 * Value shape emitted by the `inline.widenVideo` custom editor
 * (cartridges/bm_app_storefrontnext_smg/.../editors/inline/widenVideo.js).
 */
export interface WidenVideoValue {
    videoEmbedCode?: string;
    videoEmbedCodeClean?: string;
    videoTitle?: string;
    videoTitleColor?: string;
    version?: number;
    versionName?: string;
}

/**
 * Value shape emitted by the `inline.highlight` custom editor
 * (cartridges/bm_app_storefrontnext_smg/.../editors/inline/highlight.js).
 */
export interface HighlightValue {
    backgroundColor?: string;
    textColor?: string;
    version?: number;
    versionName?: string;
}

type MediaType = 'image' | 'video';
type HighlightBg = 'none' | 'blue' | 'green' | 'orange';
type HighlightText = 'light' | 'dark';
type TextAlignment = 'left' | 'center' | 'right';
// "none" removes the paper band entirely and hands the background to the Default Settings theme.
type RipperTone = 'dark' | 'light' | 'none';

const MEDIA_TYPES = ['image', 'video'] as const;
const HIGHLIGHT_BGS = ['none', 'blue', 'green', 'orange'] as const;
const HIGHLIGHT_TEXTS = ['light', 'dark'] as const;
const RIPPER_TONES = ['dark', 'light', 'none'] as const;
const TEXT_ALIGNMENTS = ['left', 'center', 'right'] as const;

/** Text-alignment option → the Tailwind utility applied to the hero content (title + after-title sub-header). */
const ALIGN_CLASS: Record<TextAlignment, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

/** The whole-component background is the OPPOSITE tone of the ripper paper (see ticket UCR-2698). */
const OPPOSITE_BG: Record<RipperPaperTone, string> = {
    dark: 'var(--bm-organic-white)',
    light: 'var(--bm-ink)',
};

function normalize<T extends string>(value: string | undefined, allowed: readonly T[], fallback: T): T {
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/**
 * Derive a direct MP4 delivery URL from a Widen video embed so the Hero can play it as a
 * muted, autoplaying background `<video>` (the embed itself is a chrome'd player, unusable
 * as a background). Widen serves the file at `/content/<hash>/mp4/<file>`, which 303-redirects
 * to the CDN. Returns undefined if the embed isn't a recognizable Widen video view URL.
 */
export function widenVideoFileUrl(value?: WidenVideoValue): string | undefined {
    const embed = value?.videoEmbedCodeClean?.trim() || value?.videoEmbedCode?.trim();
    if (!embed) return undefined;
    const src = embed.match(/src="([^"]+)"/i)?.[1] ?? (embed.startsWith('http') ? embed : undefined);
    if (!src) return undefined;
    try {
        const url = new URL(src);
        const match = url.pathname.match(/^\/view\/video\/([^/]+)\/(.+)$/);
        if (!match) return undefined;
        url.pathname = `/content/${match[1]}/mp4/${match[2]}`;
        return url.toString();
    } catch {
        return undefined;
    }
}

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
@Component('hero', {
    name: 'Hero',
    description:
        'Brand hero for any page: an h1 title and contained media (Widen image or video) on a torn ripper-paper band, with a highlight-style sub-header overlaying the media. The ripper-paper tone sets the band color/texture; the rest of the component takes the opposite tone.',
    group: '2_BlackMagic_Assets',
})
@RegionDefinition([])
export class HeroMetadata {
    @AttributeDefinition({
        id: 'settings',
        name: 'Default Settings',
        description:
            'Theme (light/dark), CSS class, and ID. When Ripper Paper is "None", this theme sets the hero background and text color.',
        type: 'custom',
        editorDefinition: { type: 'inline.defaultSettings' },
    })
    settings?: DefaultSettingsValue;

    @AttributeDefinition({
        id: 'title',
        name: 'Title',
        description: 'Headline, rendered as the page h1.',
        type: 'string',
        required: true,
    })
    title?: string;

    @AttributeDefinition({
        id: 'mediaType',
        name: 'Media Type',
        description: 'Whether the media slot shows the image or the video.',
        type: 'enum',
        values: ['image', 'video'],
        defaultValue: 'image',
    })
    mediaType?: string;

    @AttributeDefinition({
        id: 'image',
        name: 'Image',
        description: 'Widen DAM image (used when Media Type = image).',
        type: 'custom',
        editorDefinition: {
            type: 'inline.widenImage',
            // Match the standalone Widen Image component: dimensions are auto-captured, so hide the
            // manual size/alignment/quality/focal-point controls.
            configuration: {
                showSizes: false,
                showImageAlignment: false,
                showQualitySlider: false,
                showGridCoordinate: false,
            },
        },
    })
    image?: WidenImageValue;

    @AttributeDefinition({
        id: 'video',
        name: 'Video',
        description: 'Widen DAM video (used when Media Type = video).',
        type: 'custom',
        editorDefinition: { type: 'inline.widenVideo' },
    })
    video?: WidenVideoValue;

    @AttributeDefinition({
        id: 'subheader',
        name: 'Sub-header',
        description: 'Short highlight text overlaying the media, lower-left.',
        type: 'string',
        required: false,
    })
    subheader?: string;

    @AttributeDefinition({
        id: 'highlight',
        name: 'Sub-header Highlight',
        description:
            'Highlight background (none/blue/green/orange) + text color (light/dark). "None" shows the sub-header with no highlight band.',
        type: 'custom',
        editorDefinition: { type: 'inline.highlight' },
    })
    highlight?: HighlightValue;

    @AttributeDefinition({
        id: 'textAlignment',
        name: 'Text Alignment',
        description: 'Alignment of the title (and the sub-header when it is placed after the title).',
        type: 'enum',
        required: false,
        values: ['left', 'center', 'right'],
        defaultValue: 'left',
    })
    textAlignment?: string;

    @AttributeDefinition({
        id: 'subheaderOverMedia',
        name: 'Overlay Sub-header on Media',
        description:
            'Off (default): the sub-header sits just after the title. On: it overlays the media in the lower-right.',
        type: 'boolean',
        defaultValue: false,
    })
    subheaderOverMedia?: boolean;

    @AttributeDefinition({
        id: 'ripperTone',
        name: 'Ripper Paper',
        description:
            'Color/texture of the ripper-paper band (dark/light); the rest of the hero takes the opposite tone. "None" removes the paper and uses the Default Settings background instead.',
        type: 'enum',
        values: ['dark', 'light', 'none'],
        defaultValue: 'dark',
    })
    ripperTone?: string;
}
/* v8 ignore stop */

export interface HeroProps {
    settings?: DefaultSettingsValue;
    title?: string;
    mediaType?: string;
    image?: WidenImageValue;
    video?: WidenVideoValue;
    subheader?: string;
    highlight?: HighlightValue;
    textAlignment?: string;
    subheaderOverMedia?: boolean;
    ripperTone?: string;
}

function HeroMedia({ mediaType, image, video }: Pick<HeroProps, 'mediaType' | 'image' | 'video'>): ReactElement {
    const type = normalize<MediaType>(mediaType, MEDIA_TYPES, 'image');

    if (type === 'video') {
        const fileUrl = widenVideoFileUrl(video);
        if (!fileUrl) {
            return (
                <div className="flex h-full w-full items-center justify-center bg-muted text-sm text-muted-foreground">
                    Add a Widen video embed code
                </div>
            );
        }
        // Decorative background video: muted + autoplay + loop, no controls. `muted` and
        // `playsInline` are required for autoplay (esp. iOS/Safari). aria-hidden since it's purely visual.
        return (
            <video
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true">
                <source src={fileUrl} type="video/mp4" />
            </video>
        );
    }

    const fileURL = image?.fileURL?.trim();
    const webpURL = image?.webpURL?.trim();
    if (!fileURL) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-muted text-sm text-muted-foreground">
                Add a Widen image embed code
            </div>
        );
    }
    return (
        <picture>
            {webpURL && <source srcSet={webpURL} type="image/webp" />}
            <img
                src={fileURL}
                alt={image?.alternativeText ?? ''}
                fetchPriority="high"
                className="absolute inset-0 h-full w-full object-cover"
            />
        </picture>
    );
}

/**
 * Hero — brand hero component (UCR-2698). Renders an h1 title and contained media
 * (Widen image/video) on a torn ripper-paper band, with a highlight sub-header. By
 * default the sub-header sits just after the title (following Text Alignment); it can
 * instead overlay the media's lower-right. The sub-header highlight can be none/blue/
 * green/orange. The ripper tone sets the band; the component background is the opposite
 * tone, revealed through the band's torn bottom edge.
 */
export default function Hero({
    settings,
    title,
    mediaType,
    image,
    video,
    subheader,
    highlight,
    textAlignment,
    subheaderOverMedia,
    ripperTone,
}: HeroProps): ReactElement {
    const ripper = normalize<RipperTone>(ripperTone, RIPPER_TONES, 'dark');
    // "none" drops the paper band and lets the Default Settings theme own the background + text color.
    const noPaper = ripper === 'none';
    // When the paper shows, its tone (dark/light) drives the opposite-tone background + the dark invert.
    const paperTone: RipperPaperTone = ripper === 'light' ? 'light' : 'dark';
    const highlightBg = normalize<HighlightBg>(highlight?.backgroundColor, HIGHLIGHT_BGS, 'green');
    const highlightText = normalize<HighlightText>(highlight?.textColor, HIGHLIGHT_TEXTS, 'light');
    const align = normalize<TextAlignment>(textAlignment, TEXT_ALIGNMENTS, 'left');
    const hasSubheader = Boolean(subheader?.trim());
    const overlay = Boolean(subheaderOverMedia);
    // Sub-header sits directly under the title (in flow) — tighten the title's bottom margin.
    const afterTitleSubheader = hasSubheader && !overlay;

    // Sub-header look: highlight band background (omitted for "none") + text color + display type.
    // The band's padding differs by placement, so it's applied at each call site (below):
    //  - overlay: `highlight-padded` (the design-system inline band padding)
    //  - after-title: a snug `p-[0.5rem]` box (no fixed 0.5rem DS token — the `s` token is responsive)
    const hasBand = highlightBg !== 'none';
    const subheaderBase = cn(
        hasBand ? cn(`highlight-${highlightBg}`, `highlight-text-${highlightText}`) : `highlight-text-${highlightText}`,
        'font-display text-5xl font-extrabold uppercase leading-[0.85] tracking-[-0.05em] md:text-8xl lg:text-9xl'
    );

    return (
        // Default Settings supplies the theme class (bm-theme-light/dark) + custom class/id. When a
        // paper tone is chosen, the inline opposite-tone background wins over the theme (the paper look);
        // when Ripper Paper is "None", there's no paper and no inline override, so the theme owns the bg.
        <section
            {...defaultSettingsProps(settings, 'relative w-full overflow-hidden')}
            data-slot="hero"
            style={noPaper ? undefined : { backgroundColor: OPPOSITE_BG[paperTone] }}>
            {/* Full-bleed paper texture: full window width + full hero height (stretched), with the
                torn bottom edge at the base, revealing the opposite-tone background below it.
                Dark tone inverts the light sheet (paper light→dark, reveal dark→light). Omitted for "none". */}
            {!noPaper && (
                <img
                    src={paperTexture}
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 h-full w-full"
                    style={paperTone === 'dark' ? { filter: 'invert(1)' } : undefined}
                />
            )}

            {/* Content aligned to the header's content container (logo/nav). With paper, text contrasts
                it (dark text on the light sheet, light text on the dark sheet); with "none" the text
                inherits the Default Settings theme color. Text Alignment aligns the title (and the
                after-title sub-header); the media stays full-width. */}
            <div
                data-slot="hero-content"
                className={cn('relative section-container pt-6 pb-12 md:pb-[88px]', ALIGN_CLASS[align])}
                style={noPaper ? undefined : { color: OPPOSITE_BG[paperTone] }}>
                {/* Title — +20px extra horizontal padding (desktop only; mobile uses the gutter).
                    A tight 0.08em bottom margin when the sub-header sits right under it; otherwise the
                    normal gap above the media. */}
                {title?.trim() && (
                    <h1
                        className={cn(
                            'font-display text-5xl font-extrabold uppercase leading-[0.85] tracking-[-0.04em] md:px-5 md:text-8xl',
                            afterTitleSubheader ? 'mb-[0.08em]' : 'mb-6 md:mb-8'
                        )}>
                        {title}
                    </h1>
                )}

                {/* Sub-header — default placement: just after the title, in flow, following Text Alignment.
                    Cap-trim the line box so the caps sit evenly in the band (same as the overlay), with a
                    snug 0.5rem box (not the wider `highlight-padded`). */}
                {hasSubheader && !overlay && (
                    <div className="mb-6 md:mb-8 md:px-5">
                        <span
                            data-slot="hero-subheader"
                            className={cn(
                                'inline-block',
                                subheaderBase,
                                hasBand && '[text-box-edge:cap_alphabetic] [text-box-trim:trim-both] p-[0.5rem]'
                            )}>
                            {subheader}
                        </span>
                    </div>
                )}

                {/* Video — +60px extra horizontal padding (desktop only; mobile uses the gutter). */}
                <div className="md:px-[60px]">
                    <figure className="relative aspect-[4/3] w-full md:aspect-[16/9]">
                        <HeroMedia mediaType={mediaType} image={image} video={video} />
                    </figure>
                </div>
            </div>

            {/* Sub-header — overlay placement (optional): oversized highlight flush to the component's
                right edge. On mobile it overlaps the video's bottom; on desktop the lower-right corner. */}
            {hasSubheader && overlay && (
                <span
                    data-slot="hero-subheader"
                    className={cn(
                        'absolute right-0 bottom-7 md:bottom-0',
                        subheaderBase,
                        // Trim the line box to cap height so the caps sit evenly in the band (was ~16px
                        // above / 2px below), then a snug symmetric pad; horizontal reduced from 0.3em.
                        hasBand && '[text-box-edge:cap_alphabetic] [text-box-trim:trim-both] px-[0.05em] py-[0.05em]'
                    )}>
                    {subheader}
                </span>
            )}
        </section>
    );
}
